import { getGlobal, noStorageSupport } from "../lib/get-global";
import { MetadataPlugin } from "./metadata";

export const createFingerprintPlugin: MetadataPlugin<number> = function () {
  const globalThis = getGlobal();
  let fingerprint = getPersistedValue();

  function getPersistedValue(): number {
    if (noStorageSupport(globalThis)) {
      return undefined;
    }

    const FINGERPRINT_KEY = "spindl_fingerprint";
    const value = globalThis.localStorage.getItem(FINGERPRINT_KEY);

    if (!value) {
      return undefined;
    }

    const int = parseInt(value);
    return isNaN(int) ? undefined : int;
  }

  async function getValue() {
    if (!fingerprint) {
      fingerprint = await new Promise<number>((resolve) => {
        if (
          typeof globalThis === "undefined" ||
          typeof navigator === "undefined" ||
          typeof screen === "undefined" ||
          typeof document === "undefined"
        ) {
          resolve(undefined);
        }

        const items = [
          navigator.userAgent,
          getScreenPrint(),
          getPlugins(),
          getFonts(),
          isLocalStorage(globalThis),
          isSessionStorage(globalThis),
          getTimeZone(),
          getLanguage(),
          getSystemLanguage(),
          isCookie(),
          getCanvasPrint(),
        ];

        const key = items.join("|");
        const seed = 256;

        resolve(cyrb53(key, seed));
      });
    }
    return { fingerprint };
  }

  function getScreenPrint() {
    return (
      "Current Resolution: " +
      getCurrentResolution() +
      ", Available Resolution: " +
      getAvailableResolution() +
      ", Color Depth: " +
      getColorDepth() +
      ", Device XDPI: " +
      getDeviceXDPI() +
      ", Device YDPI: " +
      getDeviceYDPI() +
      ", Pixel Depth: " +
      getPixelDepth()
    );
  }

  function getColorDepth() {
    return screen.colorDepth;
  }

  function getCurrentResolution() {
    return screen.width + "x" + screen.height;
  }

  function getAvailableResolution() {
    return screen.availWidth + "x" + screen.availHeight;
  }

  function getDeviceXDPI() {
    return (screen as any).deviceXDPI;
  }

  function getDeviceYDPI() {
    return (screen as any).deviceYDPI;
  }

  function getPixelDepth() {
    return screen.pixelDepth;
  }

  function getPlugins() {
    let pluginsList = "";

    for (let i = 0; i < navigator.plugins.length; i++) {
      if (i == navigator.plugins.length - 1) {
        pluginsList += navigator.plugins[i].name;
      } else {
        pluginsList += navigator.plugins[i].name + ", ";
      }
    }
    return pluginsList;
  }

  function isLocalStorage(global: typeof globalThis) {
    try {
      return !!global.localStorage;
    } catch (e) {
      return true;
    }
  }

  function isSessionStorage(global: typeof globalThis) {
    try {
      return !!global.sessionStorage;
    } catch (e) {
      return true;
    }
  }

  function getTimeZone() {
    let result;
    const rightNow = new Date();
    let myNumber = -(rightNow.getTimezoneOffset() / 60);
    if (myNumber < 0) {
      myNumber = myNumber * -1;
      const formattedNumber = ("0" + myNumber).slice(-2);
      result = "-" + formattedNumber;
    } else {
      const formattedNumber = ("0" + myNumber).slice(-2);
      result = "+" + formattedNumber;
    }
    return result;
  }

  function getLanguage() {
    return navigator.language;
  }

  function getSystemLanguage() {
    return (navigator as any).systemLanguage || navigator.language;
  }

  function isCookie() {
    return navigator.cookieEnabled;
  }

  function getCanvasPrint() {
    const canvas = document.createElement("canvas");
    let ctx;

    try {
      ctx = canvas.getContext("2d");
    } catch (_) {
      return "";
    }

    // https://www.browserleaks.com/canvas#how-does-it-work
    // Text with lowercase/uppercase/punctuation symbols
    const txt = "ClientJS,org <canvas> 1.0";
    ctx.textBaseline = "top";
    // The most common type
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    // Some tricks for color mixing to increase the difference in rendering
    ctx.fillStyle = "#069";
    ctx.fillText(txt, 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText(txt, 4, 17);
    return canvas.toDataURL();
  }

  function getFonts() {
    const fontDetective = new (Detector as any)();
    const fontArray = [
      "Abadi MT Condensed Light",
      "Adobe Fangsong Std",
      "Adobe Hebrew",
      "Adobe Ming Std",
      "Agency FB",
      "Aharoni",
      "Andalus",
      "Angsana New",
      "AngsanaUPC",
      "Aparajita",
      "Arab",
      "Arabic Transparent",
      "Arabic Typesetting",
      "Arial Baltic",
      "Arial Black",
      "Arial CE",
      "Arial CYR",
      "Arial Greek",
      "Arial TUR",
      "Arial",
      "Batang",
      "BatangChe",
      "Bauhaus 93",
      "Bell MT",
      "Bitstream Vera Serif",
      "Bodoni MT",
      "Bookman Old Style",
      "Braggadocio",
      "Broadway",
      "Browallia New",
      "BrowalliaUPC",
      "Calibri Light",
      "Calibri",
      "Californian FB",
      "Cambria Math",
      "Cambria",
      "Candara",
      "Castellar",
      "Casual",
      "Centaur",
      "Century Gothic",
      "Chalkduster",
      "Colonna MT",
      "Comic Sans MS",
      "Consolas",
      "Constantia",
      "Copperplate Gothic Light",
      "Corbel",
      "Cordia New",
      "CordiaUPC",
      "Courier New Baltic",
      "Courier New CE",
      "Courier New CYR",
      "Courier New Greek",
      "Courier New TUR",
      "Courier New",
      "DFKai-SB",
      "DaunPenh",
      "David",
      "DejaVu LGC Sans Mono",
      "Desdemona",
      "DilleniaUPC",
      "DokChampa",
      "Dotum",
      "DotumChe",
      "Ebrima",
      "Engravers MT",
      "Eras Bold ITC",
      "Estrangelo Edessa",
      "EucrosiaUPC",
      "Euphemia",
      "Eurostile",
      "FangSong",
      "Forte",
      "FrankRuehl",
      "Franklin Gothic Heavy",
      "Franklin Gothic Medium",
      "FreesiaUPC",
      "French Script MT",
      "Gabriola",
      "Gautami",
      "Georgia",
      "Gigi",
      "Gisha",
      "Goudy Old Style",
      "Gulim",
      "GulimChe",
      "GungSeo",
      "Gungsuh",
      "GungsuhChe",
      "Haettenschweiler",
      "Harrington",
      "Hei S",
      "HeiT",
      "Heisei Kaku Gothic",
      "Hiragino Sans GB",
      "Impact",
      "Informal Roman",
      "IrisUPC",
      "Iskoola Pota",
      "JasmineUPC",
      "KacstOne",
      "KaiTi",
      "Kalinga",
      "Kartika",
      "Khmer UI",
      "Kino MT",
      "KodchiangUPC",
      "Kokila",
      "Kozuka Gothic Pr6N",
      "Lao UI",
      "Latha",
      "Leelawadee",
      "Levenim MT",
      "LilyUPC",
      "Lohit Gujarati",
      "Loma",
      "Lucida Bright",
      "Lucida Console",
      "Lucida Fax",
      "Lucida Sans Unicode",
      "MS Gothic",
      "MS Mincho",
      "MS PGothic",
      "MS PMincho",
      "MS Reference Sans Serif",
      "MS UI Gothic",
      "MV Boli",
      "Magneto",
      "Malgun Gothic",
      "Mangal",
      "Marlett",
      "Matura MT Script Capitals",
      "Meiryo UI",
      "Meiryo",
      "Menlo",
      "Microsoft Himalaya",
      "Microsoft JhengHei",
      "Microsoft New Tai Lue",
      "Microsoft PhagsPa",
      "Microsoft Sans Serif",
      "Microsoft Tai Le",
      "Microsoft Uighur",
      "Microsoft YaHei",
      "Microsoft Yi Baiti",
      "MingLiU",
      "MingLiU-ExtB",
      "MingLiU_HKSCS",
      "MingLiU_HKSCS-ExtB",
      "Miriam Fixed",
      "Miriam",
      "Mongolian Baiti",
      "MoolBoran",
      "NSimSun",
      "Narkisim",
      "News Gothic MT",
      "Niagara Solid",
      "Nyala",
      "PMingLiU",
      "PMingLiU-ExtB",
      "Palace Script MT",
      "Palatino Linotype",
      "Papyrus",
      "Perpetua",
      "Plantagenet Cherokee",
      "Playbill",
      "Prelude Bold",
      "Prelude Condensed Bold",
      "Prelude Condensed Medium",
      "Prelude Medium",
      "PreludeCompressedWGL Black",
      "PreludeCompressedWGL Bold",
      "PreludeCompressedWGL Light",
      "PreludeCompressedWGL Medium",
      "PreludeCondensedWGL Black",
      "PreludeCondensedWGL Bold",
      "PreludeCondensedWGL Light",
      "PreludeCondensedWGL Medium",
      "PreludeWGL Black",
      "PreludeWGL Bold",
      "PreludeWGL Light",
      "PreludeWGL Medium",
      "Raavi",
      "Rachana",
      "Rockwell",
      "Rod",
      "Sakkal Majalla",
      "Sawasdee",
      "Script MT Bold",
      "Segoe Print",
      "Segoe Script",
      "Segoe UI Light",
      "Segoe UI Semibold",
      "Segoe UI Symbol",
      "Segoe UI",
      "Shonar Bangla",
      "Showcard Gothic",
      "Shruti",
      "SimHei",
      "SimSun",
      "SimSun-ExtB",
      "Simplified Arabic Fixed",
      "Simplified Arabic",
      "Snap ITC",
      "Sylfaen",
      "Symbol",
      "Tahoma",
      "Times New Roman Baltic",
      "Times New Roman CE",
      "Times New Roman CYR",
      "Times New Roman Greek",
      "Times New Roman TUR",
      "Times New Roman",
      "TlwgMono",
      "Traditional Arabic",
      "Trebuchet MS",
      "Tunga",
      "Tw Cen MT Condensed Extra Bold",
      "Ubuntu",
      "Umpush",
      "Univers",
      "Utopia",
      "Utsaah",
      "Vani",
      "Verdana",
      "Vijaya",
      "Vladimir Script",
      "Vrinda",
      "Webdings",
      "Wide Latin",
      "Wingdings",
    ];
    let fontString = "";

    for (let i = 0; i < fontArray.length; i++) {
      if (fontDetective.detect(fontArray[i])) {
        if (i == fontArray.length - 1) {
          fontString += fontArray[i];
        } else {
          fontString += fontArray[i] + ", ";
        }
      }
    }

    return fontString;
  }

  function Detector() {
    const baseFonts = ["monospace", "sans-serif", "serif"];
    const testString = "mmmmmmmmmmlli";
    const testSize = "72px";

    const h = document.getElementsByTagName("body")[0];

    const s = document.createElement("span");
    s.style.fontSize = testSize;
    s.innerHTML = testString;
    const defaultWidth = {} as any;
    const defaultHeight = {} as any;
    for (let index in baseFonts) {
      //get the default width for the three base fonts
      s.style.fontFamily = baseFonts[index];
      h.appendChild(s);
      defaultWidth[baseFonts[index]] = s.offsetWidth; //width for the default font
      defaultHeight[baseFonts[index]] = s.offsetHeight; //height for the defualt font
      h.removeChild(s);
    }

    function detect(font: string) {
      let detected = false;
      for (let index in baseFonts) {
        s.style.fontFamily = font + "," + baseFonts[index]; // name of the font along with the base font for fallback.
        h.appendChild(s);
        const matched =
          s.offsetWidth != defaultWidth[baseFonts[index]] ||
          s.offsetHeight != defaultHeight[baseFonts[index]];
        h.removeChild(s);
        detected = detected || matched;
      }
      return detected;
    }

    this.detect = detect;
  }

  function cyrb53(str: string, seed = 0) {
    let h1 = 0xdeadbeef ^ seed,
      h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 =
      Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
      Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 =
      Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
      Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }

  return {
    getValue,
  };
};
